/**
 * Job lifecycle gate vocabulary — the single source of truth for what a
 * job's current workflow state is called in the UI.
 *
 * Every UI surface that shows a status pill, a section title that depends
 * on workflow state, or a "what happens next" line should pull from this
 * namespace via t('lifecycleGate.<bucket>.<gate>'). Direct hard-coded
 * strings like "Close-out", "Confirm work completed", or
 * "Awaiting verification" are now considered orphans.
 */
export const lifecycleGate = {
  /** Long-form label used on status pills, section titles, dock-bar text. */
  label: {
    draft: 'Draft',
    open: 'Open',
    /** Accepted but not yet dated: the `open` gate, but the job IS accepted. */
    accepted: 'Accepted',
    awaiting_assignee_response: 'Awaiting acceptance',
    assignee_declined: 'Rejected by assignee',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    paused: 'Paused',
    completion_submitted: 'Awaiting sign-off',
    completion_rejected: 'Rework requested',
    completion_verified: 'Signed off',
    awaiting_follow_on_approval: 'Pending approval',
    cancelled: 'Cancelled',
    skipped: 'Skipped',
  },
  /** Short label for chips and dense list rows where space is tight. */
  shortLabel: {
    draft: 'Draft',
    open: 'Open',
    accepted: 'Accepted',
    awaiting_assignee_response: 'Awaiting',
    assignee_declined: 'Declined',
    scheduled: 'Scheduled',
    in_progress: 'Running',
    paused: 'Paused',
    completion_submitted: 'Submitted',
    completion_rejected: 'Rework',
    completion_verified: 'Signed off',
    awaiting_follow_on_approval: 'Approval',
    cancelled: 'Cancelled',
    skipped: 'Skipped',
  },
  /**
   * One-line "next actor" copy used in subtitle slots and dock fallback
   * strings. Always written as "<who> <does what>" so the user can scan
   * one column for the next move. `{{name}}` is the mover's name where one
   * resolves; the caller passes the role noun ("Assignee", "Owner") when
   * nobody does.
   */
  nextActor: {
    draft: '{{name}} finalizes draft',
    open: '{{name}} accepts the job',
    awaiting_assignee_response: '{{name}} accepts or declines',
    assignee_declined: '{{name}} reassigns the job',
    scheduled: '{{name}} starts work',
    in_progress: '{{name}} submits completion',
    paused: '{{name}} resumes when unblocked',
    completion_submitted: '{{name}} reviews completion',
    completion_rejected: '{{name}} performs rework',
    completion_verified: 'Job is closed',
    awaiting_follow_on_approval: '{{name}} decides',
    cancelled: 'No further action',
    skipped: 'No further action',
  },
  /** The same moves addressed to the person who has to make them. */
  nextActorYou: {
    draft: 'You finalize the draft',
    open: 'You accept the job',
    awaiting_assignee_response: 'You accept or decline',
    assignee_declined: 'You reassign the job',
    scheduled: 'You start work',
    in_progress: 'You submit completion',
    paused: 'You resume when unblocked',
    completion_rejected: 'You perform rework',
    completion_submitted: 'You review the completion',
    evidence_requested: 'You add the evidence asked for and resubmit',
    awaiting_follow_on_approval: 'You approve or reject the follow-on',
  },
} as const;
